import React from 'react';
import PropTypes from 'prop-types';
import { LuBuilding2, LuCalendarClock, LuCoins, LuPaperclip, LuReceipt, LuToggleRight } from 'react-icons/lu';
import { Field, SelectInput, TextInput } from '../../../components/form/FormControls';
import useBudgetText from '../budgetText';
import { FileDropZone, FileRow, FormSection } from './formBits';

const CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP'];
const AMOUNT_FIELDS = [
  ['Iskonto', 'discount'],
  ['FaturaDisiMasraflari', 'extraCosts'],
  ['SatirSayisi', 'lineCount', '1'],
  ['BirimFiyatMiktarTutari', 'lineTotal'],
  ['OdenecekTutar', 'payable'],
  ['VergilerHaricTutar', 'taxExcluded'],
  ['VergilerDahilTutar', 'taxIncluded'],
];

/**
 * The e-invoice fields of "Yeni Gelir / Gider Ekle" (same fields, names and
 * required ones as before) and the optional attachments. `party` is 'Musteri'
 * for income and 'Gonderici' for expenses; the dialog keeps all the state.
 */
const InvoiceFormFields = ({ party, idPrefix, formData, onChange, statusOptions, files }) => {
  const { label, statusLabel } = useBudgetText();
  const income = party === 'Musteri';
  const id = (name) => `${idPrefix}-${name}`;
  const text = (name, extra = {}) => (
    <TextInput id={id(name)} name={name} value={formData[name]} onChange={onChange} {...extra} />
  );

  return (
    <div className="tw-space-y-7">
      <FormSection icon={LuReceipt} title={label('form.sections.invoice')}>
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('columns.gib')} htmlFor={id('GibNumarasi')}>{text('GibNumarasi')}</Field>
          <Field label={label('columns.draft')} htmlFor={id('TaslakNo')}>{text('TaslakNo')}</Field>
          <Field label={label('columns.scenario')} htmlFor={id('Senaryo')}>{text('Senaryo', { placeholder: label('form.scenarioPlaceholder') })}</Field>
          <Field label={label('columns.invoiceType')} htmlFor={id('FaturaTipi')}>{text('FaturaTipi', { placeholder: label('form.invoiceTypePlaceholder') })}</Field>
          <Field label={label('columns.ettn')} htmlFor={id('Ettn')}>{text('Ettn')}</Field>
          <Field label={label('columns.envelopeUuid')} htmlFor={id('ZarfUuid')}>{text('ZarfUuid')}</Field>
          <Field label={label('columns.envelopeStatus')} htmlFor={id('ZarfDurumu')}>{text('ZarfDurumu')}</Field>
        </div>
      </FormSection>

      <FormSection icon={LuCalendarClock} title={label('form.sections.dates')}>
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('columns.createdDate')} htmlFor={id('OlusturmaTarihi')}>{text('OlusturmaTarihi', { type: 'date' })}</Field>
          <Field label={label('columns.createdTime')} htmlFor={id('OlusturmaZamani')}>{text('OlusturmaZamani', { type: 'time', step: '1' })}</Field>
          <Field label={label('columns.documentDate')} htmlFor={id('BelgeTarihi')} required>{text('BelgeTarihi', { type: 'date', required: true })}</Field>
          <Field label={label('columns.documentTime')} htmlFor={id('BelgeZamani')}>{text('BelgeZamani', { type: 'time', step: '1' })}</Field>
        </div>
      </FormSection>

      <FormSection icon={LuBuilding2} title={label(income ? 'form.sections.customer' : 'form.sections.sender')}>
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label(income ? 'columns.customerName' : 'columns.senderName')} htmlFor={id(`${party}Unvan`)} required className="sm:tw-col-span-2">
            {text(`${party}Unvan`, { required: true, placeholder: label('form.unvanPlaceholder') })}
          </Field>
          <Field label={label(income ? 'columns.customerTax' : 'columns.senderTax')} htmlFor={id(`${party}TcknVkn`)}>{text(`${party}TcknVkn`)}</Field>
          <Field label={label(income ? 'columns.customerTag' : 'columns.senderTag')} htmlFor={id(`${party}Etiketi`)}>{text(`${party}Etiketi`)}</Field>
        </div>
      </FormSection>

      <FormSection icon={LuCoins} title={label('form.sections.amounts')}>
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('columns.amount')} htmlFor={id('Tutar')} required>
            {text('Tutar', { type: 'number', step: '0.01', required: true, className: 'tw-tabular-nums' })}
          </Field>
          <Field label={label('columns.currency')} htmlFor={id('ParaBirimi')}>
            <SelectInput id={id('ParaBirimi')} name="ParaBirimi" value={formData.ParaBirimi} onChange={onChange} options={CURRENCIES.map((code) => ({ value: code, label: code }))} />
          </Field>
          {AMOUNT_FIELDS.map(([name, labelId, step]) => (
            <Field key={name} label={label(`columns.${labelId}`)} htmlFor={id(name)}>
              {text(name, { type: 'number', step: step || '0.01', className: 'tw-tabular-nums' })}
            </Field>
          ))}
        </div>
      </FormSection>

      <FormSection icon={LuToggleRight} title={label('form.sections.status')}>
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('columns.paymentStatus')} htmlFor={id('ÖdemeDurumu')}>
            <SelectInput id={id('ÖdemeDurumu')} name="ÖdemeDurumu" value={formData['ÖdemeDurumu']} onChange={onChange} options={statusOptions.map((value) => ({ value, label: statusLabel(value) }))} />
          </Field>
          <Field label={label('form.activeStatus')} htmlFor={id('AktifDurum')}>
            <SelectInput id={id('AktifDurum')} name="AktifDurum" value={formData.AktifDurum} onChange={onChange} options={['Aktif', 'Pasif'].map((value) => ({ value, label: statusLabel(value) }))} />
          </Field>
        </div>
      </FormSection>

      <FormSection icon={LuPaperclip} title={label('form.sections.files')}>
        <FileDropZone
          title={label('form.dropTitle')}
          hint={label('form.dropHint')}
          isDragging={files.isDragging}
          inputRef={files.inputRef}
          accept="*/*"
          multiple
          onDragOver={files.onDragOver}
          onDragLeave={files.onDragLeave}
          onDrop={files.onDrop}
          onInputChange={files.onInputChange}
        />
        {files.selectedFiles.length > 0 && (
          <div className="tw-space-y-2">
            {files.selectedFiles.map((file, index) => (
              <FileRow key={`${file.name}-${file.size}-${index}`} name={file.name} size={file.size} onRemove={() => files.onRemove(index)} />
            ))}
          </div>
        )}
      </FormSection>
    </div>
  );
};

InvoiceFormFields.propTypes = {
  party: PropTypes.oneOf(['Musteri', 'Gonderici']).isRequired,
  idPrefix: PropTypes.string.isRequired,
  formData: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  statusOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  files: PropTypes.shape({
    selectedFiles: PropTypes.array.isRequired,
    isDragging: PropTypes.bool,
    inputRef: PropTypes.object.isRequired,
    onDragOver: PropTypes.func.isRequired,
    onDragLeave: PropTypes.func.isRequired,
    onDrop: PropTypes.func.isRequired,
    onInputChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
  }).isRequired,
};

export default InvoiceFormFields;
